import React from "react";
import Dialog from "@material-ui/core/Dialog";
import Button from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";

import brdidLogo from "../../assets/brdid-logo.png";

const MONO = "ui-monospace,Menlo,Consolas,monospace";
const SANS = '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Arial,sans-serif';
const CHAR = "#2C353D";
const OK = "#1E9E62";
const DANGER = "#C0453B";

// Modal de progresso da importação + testes (BR DID -> Numera). Dirigido pelo STATUS
// REAL do número (testStatus/testInbound/testOutbound, ao vivo via socket): não há
// timers fake. É informativo e fechável; o resultado também aparece no badge da lista.
const useStyles = makeStyles(() => ({
  paper: { width: 488, maxWidth: "94vw", borderRadius: 16, padding: "26px 30px 22px", textAlign: "center", fontFamily: SANS, color: "#1B222A" },
  kicker: { fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: "#929AA4", fontWeight: 700 },
  num: { fontFamily: MONO, fontSize: 16, marginTop: 5 },

  transfer: { display: "flex", alignItems: "center", justifyContent: "center", margin: "24px auto 6px", height: 96 },
  endpoint: { display: "flex", flexDirection: "column", alignItems: "center", gap: 8, width: 78, flex: "none" },
  tile: { width: 60, height: 60, borderRadius: 15, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" },
  src: { background: "#F1F5EC", border: "1px solid #E2EBD6", "& img": { width: 40, height: "auto" } },
  dst: { background: CHAR, "& svg": { width: 32 } },
  name: { fontSize: 11, fontWeight: 600, color: "#5B6470" },
  nameMut: { color: "#929AA4" },

  // trilho largo (min 150px) pra dar pra ver as bolinhas viajando
  wire: { flex: 1, minWidth: 150, height: 60, position: "relative", display: "flex", alignItems: "center", margin: "0 6px" },
  line: { position: "absolute", left: 0, right: 0, top: "50%", height: 2, background: "repeating-linear-gradient(90deg,#D7DCE2 0 6px,transparent 6px 12px)" },
  lineDone: { background: "#CFE6D6 !important" },
  "@keyframes flow": {
    "0%": { left: "2%", opacity: 0 },
    "12%": { opacity: 1 },
    "88%": { opacity: 1 },
    "100%": { left: "98%", opacity: 0 },
  },
  pkt: {
    position: "absolute",
    top: "50%",
    left: "2%",
    width: 12,
    height: 12,
    borderRadius: "50%",
    transform: "translateY(-50%)",
    background: "radial-gradient(circle at 50% 38%,#E6FF7A,#9BD61E 62%,#6FA800)",
    boxShadow: "0 0 9px rgba(155,214,30,.8)",
    animation: "$flow 1.9s linear infinite",
    "&:nth-child(2)": { animationDelay: "0s" },
    "&:nth-child(3)": { animationDelay: ".63s" },
    "&:nth-child(4)": { animationDelay: "1.26s" },
  },
  pktHidden: { display: "none" },
  ring2: { position: "absolute", inset: -6, borderRadius: 19, border: `2px solid ${OK}`, opacity: 0, transform: "scale(.9)", transition: ".4s" },
  ringOn: { opacity: 1, transform: "scale(1)" },

  h: { fontSize: 17, fontWeight: 600, margin: "14px 0 0" },
  note: { background: "#F2F4F6", borderRadius: 10, padding: "12px 14px", margin: "16px 0 4px", fontSize: 13, color: "#5B6470", lineHeight: 1.5, "& b": { color: "#1B222A" } },

  steps: { textAlign: "left", margin: "16px 4px 6px", display: "flex", flexDirection: "column", gap: 2 },
  step: { display: "flex", alignItems: "center", gap: 11, padding: "8px 4px", fontSize: 13.5, color: "#929AA4" },
  stepActive: { color: "#1B222A" },
  stepDone: { color: "#5B6470" },
  ic: { width: 24, height: 24, flex: "none", display: "flex", alignItems: "center", justifyContent: "center", "& svg": { width: 18, height: 18 } },
  pend: { width: 10, height: 10, borderRadius: "50%", border: "2px solid #C8CDD3" },
  "@keyframes sp": { to: { transform: "rotate(360deg)" } },
  sp2: { width: 16, height: 16, border: "2px solid #D5DAE0", borderTopColor: CHAR, borderRadius: "50%", animation: "$sp .8s linear infinite" },
  lab: { "& b": { fontWeight: 600 }, "& small": { display: "block", fontSize: 11.5, color: "#929AA4" } },

  foot: { display: "flex", alignItems: "center", gap: 10, marginTop: 14, paddingTop: 14, borderTop: "1px solid #EEF0F3" },
  tiein: { fontSize: 11.5, color: "#929AA4" },
  btn: { marginLeft: "auto", background: CHAR, color: "#fff", textTransform: "none", fontSize: 13.5, fontWeight: 600, padding: "10px 18px", borderRadius: 8, "&:hover": { background: "#1f262d" } },
}));

const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke={OK} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 L9 17 L4 12" /></svg>
);
const Cross = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke={DANGER} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6 L18 18 M18 6 L6 18" /></svg>
);

const TERMINAL = ["passed", "partial", "failed"];
const isResolved = v => v === "ok" || v === "fail";

const ImportProgressModal = ({ open, onClose, label, num, error }) => {
  const classes = useStyles();

  // status REAL do número (ao vivo via parent/socket). num pode faltar no 1º render.
  const st = num && num.testStatus;
  const inb = num && num.testInbound; // null | pending | ok | fail
  const out = num && num.testOutbound;
  const terminal = TERMINAL.includes(st);
  // as chamadas começam depois da espera de propagação; detectamos pelo 1º resultado
  const callsStarted = isResolved(inb) || isResolved(out) || terminal;
  const passed = st === "passed";

  // importação concluída = o número já existe na conta (a requisição respondeu)
  const imported = !!num;

  const legState = v => (v === "ok" ? "done" : v === "fail" ? "fail" : "spin");
  const s0 = error ? "fail" : imported ? "done" : "spin";
  const s1 = error ? "pend" : !imported ? "pend" : callsStarted ? "done" : "spin";
  const s2 = !callsStarted ? "pend" : legState(inb);
  const s3 = !callsStarted ? "pend" : legState(out);

  const title = error
    ? error
    : !imported
    ? "Importando o número da BR DID…"
    : !callsStarted
    ? "Ativando o número na operadora…"
    : !terminal
    ? "Testando as chamadas…"
    : passed
    ? "Pronto! Número importado e funcionando."
    : st === "partial"
    ? "Importado — uma direção falhou no teste."
    : "Importado, mas o teste das chamadas falhou.";

  const icon = stt =>
    stt === "done" ? <Check /> : stt === "fail" ? <Cross /> : stt === "spin" ? <span className={classes.sp2} /> : <span className={classes.pend} />;
  const stepCls = stt => `${classes.step} ${stt === "spin" ? classes.stepActive : ""} ${stt === "done" || stt === "fail" ? classes.stepDone : ""}`;

  const STEPS = [
    [s0, "Importando da BR DID", "trazendo o número + roteamento"],
    [s1, "Ativando na operadora", "a BR DID leva cerca de 1 minuto para liberar o número"],
    [s2, "Teste de recebimento", "chamada de entrada"],
    [s3, "Teste de discagem", "chamada de saída"],
  ];

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ className: classes.paper }}>
      <div className={classes.kicker}>Importando da BR DID</div>
      {label && <div className={classes.num}>{label}</div>}

      <div className={classes.transfer}>
        <div className={classes.endpoint}>
          <div className={`${classes.tile} ${classes.src}`}><img src={brdidLogo} alt="BR DID" /></div>
          <span className={`${classes.name} ${classes.nameMut}`}>BR DID</span>
        </div>

        <div className={classes.wire}>
          <div className={`${classes.line} ${passed ? classes.lineDone : ""}`} />
          {!terminal && !error && <><span className={classes.pkt} /><span className={classes.pkt} /><span className={classes.pkt} /></>}
        </div>

        <div className={classes.endpoint}>
          <div className={`${classes.tile} ${classes.dst}`}>
            <div className={`${classes.ring2} ${passed ? classes.ringOn : ""}`} />
            <svg viewBox="0 0 37.74 24.04">
              <path fill="#BBFF00" d="M23.94,16.02h-5.99s6.47-10.92,6.47-10.92l-12.22.06-5.9,10.09-6.3-.04L9.09,0h18.08c2.82.01,4.56,3.09,3.1,5.5l-6.33,10.52Z" />
              <path fill="#BBFF00" d="M13.8,8.02h5.99s-6.47,10.92-6.47,10.92l12.22-.06,5.9-10.09,6.3.04-9.09,15.21H9.53c-2.36-.01-3.82-2.59-2.6-4.61l6.86-11.41Z" />
            </svg>
          </div>
          <span className={classes.name}>Numera</span>
        </div>
      </div>

      <h2 className={classes.h}>{title}</h2>

      <div className={classes.note}>
        <b>Você pode fechar esta janela.</b>
        <br />
        A importação e os testes continuam em segundo plano — o resultado aparece no número, na lista.
      </div>

      <div className={classes.steps}>
        {STEPS.map(([stt, b, sub], i) => (
          <div key={i} className={stepCls(stt)}>
            <span className={classes.ic}>{icon(stt)}</span>
            <span className={classes.lab}>
              <b>{b}</b>
              <small>{sub}</small>
            </span>
          </div>
        ))}
      </div>

      <div className={classes.foot}>
        <Button className={classes.btn} onClick={onClose}>Fechar</Button>
      </div>
    </Dialog>
  );
};

export default ImportProgressModal;
